import React, { useState, useCallback } from 'react';
import { Search, Sparkles, Linkedin, Globe, Building2, Briefcase, ExternalLink, Mail, Phone, MapPin, Star, Download, UserCheck, Filter, X, ChevronDown, ChevronUp, CheckSquare, Square, Loader } from 'lucide-react';
import { useChat } from '../../hooks/useChat';
import PageLayout from '../../components/ui/PageLayout';

const VERIFIED_COUNT = '278,151,424';

const COMPANY_SIZE_OPTIONS = [
  { value: '1-10', label: '1–10 employees' },
  { value: '11-50', label: '11–50 employees' },
  { value: '51-200', label: '51–200 employees' },
  { value: '201-500', label: '201–500 employees' },
  { value: '501-1000', label: '501–1,000 employees' },
  { value: '1001-5000', label: '1,001–5,000 employees' },
  { value: '5001+', label: '5,001+ employees' },
];

const INDUSTRY_OPTIONS = [
  'Technology', 'SaaS', 'E-commerce', 'Financial Services', 'Healthcare',
  'Marketing & Advertising', 'Consulting', 'Manufacturing', 'Retail',
  'Education', 'Real Estate', 'Media & Entertainment', 'Logistics',
  'Legal Services', 'Non-profit'
];

const SENIORITY_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'partner', label: 'Partner' },
  { value: 'cxo', label: 'C-Level (CXO)' },
  { value: 'vp', label: 'Vice President' },
  { value: 'director', label: 'Director' },
  { value: 'manager', label: 'Manager' },
  { value: 'senior', label: 'Senior Individual Contributor' },
  { value: 'entry', label: 'Entry Level' },
  { value: 'training', label: 'Training / Intern' },
];

const POPULAR_SEARCHES = [
  { id: 1, title: 'C-Level Executives', description: 'CEOs, CTOs, CFOs and other C-suite leaders', icon: '👔', color: 'bg-blue-500', query: 'Find C-level executives like CEOs, CTOs, and CFOs at mid to large companies', link: 'Find CEOs' },
  { id: 2, title: 'Marketing Leaders', description: 'Marketing managers, directors, and VPs', icon: '🎯', color: 'bg-green-500', query: 'Find marketing leaders including marketing managers, directors, and VPs at B2B companies', link: 'Find Marketing' },
  { id: 3, title: 'Sales Professionals', description: 'Sales managers, reps, and business development', icon: '⚡', color: 'bg-purple-500', query: 'Find sales professionals including sales managers, account executives, and business development reps', link: 'Find Sales' },
  { id: 4, title: 'Tech Decision Makers', description: 'CTOs, VPs of Engineering, IT Directors', icon: '💻', color: 'bg-orange-500', query: 'Find technology decision makers like CTOs, VPs of Engineering, and IT Directors', link: 'Find Tech' },
  { id: 5, title: 'Small Business Owners', description: 'Founders, owners, and entrepreneurs', icon: '🏢', color: 'bg-pink-500', query: 'Find small business owners, founders, and entrepreneurs running companies with 1-50 employees', link: 'Find Owners' },
  { id: 6, title: 'HR & Recruiting', description: 'HR managers, talent acquisition, recruiters', icon: '🤝', color: 'bg-teal-500', query: 'Find HR managers, talent acquisition specialists, and recruiters at growing companies', link: 'Find HR' }
];

const MOCK_RESULTS = [
  { id: 1, name: 'Sarah Chen', title: 'VP of Marketing', company: 'TechFlow Inc', location: 'San Francisco, CA', email: 'sarah.chen@techflow.com', phone: '+1 (415) 555-0142', linkedin: 'linkedin.com/in/sarahchen', score: 94, verified: true, source: 'linkedin', industry: 'SaaS', companySize: '201-500', tags: ['decision-maker', 'budget-authority'] },
  { id: 2, name: 'Marcus Johnson', title: 'CEO & Founder', company: 'GrowthLabs', location: 'Austin, TX', email: 'marcus@growthlabs.io', phone: '+1 (512) 555-0198', linkedin: 'linkedin.com/in/marcusjohnson', score: 91, verified: true, source: 'linkedin', industry: 'Marketing & Advertising', companySize: '11-50', tags: ['founder', 'c-level'] },
  { id: 3, name: 'Emily Rodriguez', title: 'Director of Sales', company: 'CloudBase Corp', location: 'New York, NY', email: 'e.rodriguez@cloudbase.com', phone: '+1 (212) 555-0167', linkedin: 'linkedin.com/in/emilyrodriguez', score: 87, verified: true, source: 'google', industry: 'Technology', companySize: '51-200', tags: ['sales-leader', 'enterprise'] },
  { id: 4, name: 'David Park', title: 'CTO', company: 'DataSync AI', location: 'Seattle, WA', email: 'dpark@datasync.ai', phone: '+1 (206) 555-0134', linkedin: 'linkedin.com/in/davidpark', score: 89, verified: false, source: 'linkedin', industry: 'Technology', companySize: '51-200', tags: ['technical-decision-maker'] },
  { id: 5, name: 'Jennifer Walsh', title: 'Head of Growth', company: 'ScaleUp Ventures', location: 'Boston, MA', email: 'jwalsh@scaleup.vc', phone: '+1 (617) 555-0189', linkedin: 'linkedin.com/in/jenniferwalsh', score: 82, verified: true, source: 'linkedin', industry: 'Financial Services', companySize: '11-50', tags: ['growth', 'startup'] },
  { id: 6, name: 'Robert Kim', title: 'VP of Engineering', company: 'Nexus Systems', location: 'Chicago, IL', email: 'rkim@nexussystems.com', phone: '+1 (312) 555-0156', linkedin: 'linkedin.com/in/robertkim', score: 85, verified: true, source: 'google', industry: 'Technology', companySize: '201-500', tags: ['technical', 'vp-level'] },
];

function AdvancedFilters({ filters, onChange, onApply, onReset, activeCount }) {
  const [open, setOpen] = useState(false);

  const handleMultiToggle = (field, value) => {
    const current = filters?.[field] || [];
    const updated = current?.includes(value)
      ? current?.filter(v => v !== value)
      : [...current, value];
    onChange({ ...filters, [field]: updated });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm mb-6">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-gray-800 hover:bg-gray-50 rounded-2xl transition-colors"
      >
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-indigo-600" />
          <span>Advanced Filters</span>
          {activeCount > 0 && (
            <span className="bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">{activeCount} active</span>
          )}
        </div>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Job Title</label>
              <input
                type="text"
                value={filters?.jobTitle || ''}
                onChange={e => onChange({ ...filters, jobTitle: e?.target?.value })}
                placeholder="e.g. Marketing Director, CTO"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Location</label>
              <input
                type="text"
                value={filters?.location || ''}
                onChange={e => onChange({ ...filters, location: e?.target?.value })}
                placeholder="e.g. San Francisco, CA"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Company Size</label>
              <div className="flex flex-wrap gap-2">
                {COMPANY_SIZE_OPTIONS?.map(opt => {
                  const selected = (filters?.companySize || [])?.includes(opt?.value);
                  return (
                    <button
                      key={opt?.value}
                      onClick={() => handleMultiToggle('companySize', opt?.value)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        selected ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      {opt?.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Industry</label>
              <div className="flex flex-wrap gap-2">
                {INDUSTRY_OPTIONS?.map(ind => {
                  const selected = (filters?.industries || [])?.includes(ind);
                  return (
                    <button
                      key={ind}
                      onClick={() => handleMultiToggle('industries', ind)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        selected ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      {ind}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Seniority Level</label>
              <div className="flex flex-wrap gap-2">
                {SENIORITY_OPTIONS?.map(opt => {
                  const selected = (filters?.seniority || [])?.includes(opt?.value);
                  return (
                    <button
                      key={opt?.value}
                      onClick={() => handleMultiToggle('seniority', opt?.value)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        selected ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      {opt?.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-5 pt-4 border-t border-gray-100">
            <button
              onClick={onApply}
              className="flex-1 bg-indigo-600 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-indigo-700 transition-colors"
            >
              Apply Filters
            </button>
            <button
              onClick={onReset}
              className="px-5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PersonCard({ person, selected, onToggle, onExport }) {
  const sourceInfo = person?.source === 'linkedin'
    ? { icon: Linkedin, color: 'text-blue-600', bg: 'bg-blue-50', label: 'LinkedIn' }
    : { icon: Globe, color: 'text-red-500', bg: 'bg-red-50', label: 'Google' };
  const SourceIcon = sourceInfo?.icon;

  return (
    <div className={`bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition-all ${
      selected ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-200'
    }`}>
      <div className="flex items-start gap-4">
        <button onClick={() => onToggle(person?.id)} className="mt-1 flex-shrink-0">
          {selected
            ? <CheckSquare size={20} className="text-indigo-600" />
            : <Square size={20} className="text-gray-300 hover:text-indigo-400" />}
        </button>
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
          {person?.name?.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 text-base">{person?.name}</h3>
                {person?.verified && (
                  <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    <UserCheck size={11} /> Verified
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-0.5">{person?.title}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <Building2 size={13} className="text-gray-400" />
                <span className="text-sm text-gray-700 font-medium">{person?.company}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${sourceInfo?.bg} ${sourceInfo?.color}`}>
                <SourceIcon size={12} />
                {sourceInfo?.label}
              </div>
              <div className="flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-1 rounded-lg">
                <Star size={12} className="fill-amber-400 text-amber-400" />
                <span className="text-xs font-bold">{person?.score}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
            {person?.location && (
              <span className="flex items-center gap-1"><MapPin size={12} />{person?.location}</span>
            )}
            {person?.industry && (
              <span className="flex items-center gap-1"><Briefcase size={12} />{person?.industry}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {person?.email && (
              <a href={`mailto:${person?.email}`} className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2.5 py-1 rounded-lg transition-colors">
                <Mail size={12} />{person?.email}
              </a>
            )}
            {person?.phone && (
              <a href={`tel:${person?.phone}`} className="flex items-center gap-1.5 text-xs text-green-600 hover:text-green-800 bg-green-50 px-2.5 py-1 rounded-lg transition-colors">
                <Phone size={12} />{person?.phone}
              </a>
            )}
            {person?.linkedin && (
              <a href={`https://${person?.linkedin}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-900 bg-blue-50 px-2.5 py-1 rounded-lg transition-colors">
                <Linkedin size={12} />LinkedIn <ExternalLink size={10} />
              </a>
            )}
          </div>
          {person?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {person?.tags?.map(tag => (
                <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const AIPersonFinder = () => {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({ jobTitle: '', location: '', companySize: [], industries: [], seniority: [] });
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [aiInsights, setAiInsights] = useState('');
  const [showInsights, setShowInsights] = useState(false);

  const { sendMessage, isLoading, error: aiError } = useChat('OPEN_AI', 'gpt-4o', false);

  const activeFilterCount = [
    filters?.jobTitle,
    filters?.location,
    ...(filters?.companySize || []),
    ...(filters?.industries || []),
    ...(filters?.seniority || [])
  ]?.filter(Boolean)?.length;

  const handleSearch = useCallback(async (searchQuery) => {
    const q = searchQuery || query;
    if (!q?.trim()) return;

    setHasSearched(true);
    setSelected([]);
    setAiInsights('');
    setShowInsights(false);

    // Filter mock results based on query
    const filtered = MOCK_RESULTS?.filter(p => {
      const lq = q?.toLowerCase();
      return (
        p?.name?.toLowerCase()?.includes(lq) ||
        p?.title?.toLowerCase()?.includes(lq) ||
        p?.company?.toLowerCase()?.includes(lq) ||
        p?.industry?.toLowerCase()?.includes(lq) ||
        lq?.includes(p?.title?.toLowerCase()?.split(' ')?.[0]) ||
        (lq?.includes('ceo') && p?.title?.toLowerCase()?.includes('ceo')) ||
        (lq?.includes('cto') && p?.title?.toLowerCase()?.includes('cto')) ||
        (lq?.includes('marketing') && p?.title?.toLowerCase()?.includes('marketing')) ||
        (lq?.includes('sales') && p?.title?.toLowerCase()?.includes('sales')) ||
        (lq?.includes('founder') && p?.title?.toLowerCase()?.includes('founder')) ||
        (lq?.includes('c-level') && ['ceo', 'cto', 'cfo', 'coo']?.some(t => p?.title?.toLowerCase()?.includes(t)))
      );
    });

    setResults(filtered?.length > 0 ? filtered : MOCK_RESULTS);

    // Get AI insights
    try {
      const result = await sendMessage([
        { role: 'system', content: 'You are an expert B2B lead researcher. Provide brief, actionable insights about the search results in 2-3 sentences. Focus on outreach strategy.' },
        { role: 'user', content: `Search query: "${q}". Found ${filtered?.length > 0 ? filtered?.length : MOCK_RESULTS?.length} prospects. Provide outreach insights.` }
      ], { max_completion_tokens: 150 });

      const content = result?.choices?.[0]?.message?.content || result?.content || '';
      if (content && !result?._fallback) {
        setAiInsights(content);
        setShowInsights(true);
      }
    } catch (err) {
      // AI insights are optional, don't block the search
    }
  }, [query, sendMessage]);

  const handlePopularSearch = (item) => {
    setQuery(item?.query);
    handleSearch(item?.query);
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev?.includes(id) ? prev?.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selected?.length === results?.length) {
      setSelected([]);
    } else {
      setSelected(results?.map(r => r?.id));
    }
  };

  const handleExportSelected = () => {
    const toExport = results?.filter(r => selected?.includes(r?.id));
    const csv = [
      ['Name', 'Title', 'Company', 'Email', 'Phone', 'LinkedIn', 'Location', 'Score', 'Verified'],
      ...toExport?.map(p => [p?.name, p?.title, p?.company, p?.email, p?.phone, p?.linkedin, p?.location, p?.score, p?.verified ? 'Yes' : 'No'])
    ]?.map(row => row?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.href = url;
    a.download = `ai-person-finder-${Date.now()}.csv`;
    a?.click();
    URL?.revokeObjectURL(url);
  };

  const handleResetFilters = () => {
    setFilters({ jobTitle: '', location: '', companySize: [], industries: [], seniority: [] });
  };

  return (
    <PageLayout>
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 rounded-2xl p-8 mb-8 text-white">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-yellow-300" />
            <span className="text-sm font-medium text-indigo-200">AI-Powered Person Finder</span>
          </div>
          <h1 className="text-3xl font-bold mb-2">Find Decision-Makers Instantly</h1>
          <p className="text-indigo-200 mb-2">Search across LinkedIn, Google, and company databases to find verified contacts with emails and phone numbers.</p>
          <p className="text-sm text-indigo-300">
            <span className="font-semibold text-white">{VERIFIED_COUNT}</span> verified professionals in our database
          </p>
        </div>

        {/* Search Bar */}
        <div className="mt-6 flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-300" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e?.target?.value)}
              onKeyDown={e => e?.key === 'Enter' && handleSearch()}
              placeholder="e.g. VP of Marketing at SaaS companies in San Francisco..."
              className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur border border-white/20 rounded-xl text-white placeholder-indigo-300 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm"
            />
          </div>
          <button
            onClick={() => handleSearch()}
            disabled={isLoading || !query?.trim()}
            className="px-6 py-4 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-indigo-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
          >
            {isLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {isLoading ? 'Searching...' : 'Find People'}
          </button>
        </div>
      </div>

      {/* Popular Searches */}
      {!hasSearched && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Popular Searches</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {POPULAR_SEARCHES?.map(item => (
              <button
                key={item?.id}
                onClick={() => handlePopularSearch(item)}
                className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all text-left group"
              >
                <div className={`w-10 h-10 ${item?.color} rounded-xl flex items-center justify-center text-xl flex-shrink-0`}>
                  {item?.icon}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-indigo-700">{item?.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item?.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Advanced Filters */}
      <AdvancedFilters
        filters={filters}
        onChange={setFilters}
        onApply={() => hasSearched && handleSearch()}
        onReset={handleResetFilters}
        activeCount={activeFilterCount}
      />

      {/* AI Insights */}
      {showInsights && aiInsights && (
        <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-xl flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-indigo-900 mb-1">AI Outreach Insights</p>
            <p className="text-sm text-indigo-800">{aiInsights}</p>
          </div>
          <button onClick={() => setShowInsights(false)} className="ml-auto text-indigo-400 hover:text-indigo-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Results */}
      {hasSearched && (
        <div>
          {/* Results Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {results?.length} Prospects Found
              </h2>
              {results?.length > 0 && (
                <button
                  onClick={toggleSelectAll}
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  {selected?.length === results?.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>
            {selected?.length > 0 && (
              <button
                onClick={handleExportSelected}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Download size={16} />
                Export {selected?.length} Selected
              </button>
            )}
          </div>

          {/* Results Grid */}
          <div className="space-y-4">
            {results?.map(person => (
              <PersonCard
                key={person?.id}
                person={person}
                selected={selected?.includes(person?.id)}
                onToggle={toggleSelect}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!hasSearched && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-indigo-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Your Search</h3>
          <p className="text-gray-500 max-w-md mx-auto">Enter a search query above or click one of the popular searches to find decision-makers and key contacts.</p>
        </div>
      )}
    </PageLayout>
  );
};

export default AIPersonFinder;
